import React, { useState, useRef } from 'react';
import { Modal, Form, TextArea, Button, Icon, Image, Grid, Message } from 'semantic-ui-react';
import { StyledModal } from './SharedStyles';
import { resizeImage } from '../utils/imageUtils';
import * as api from '../services/api';

const TaskGenerationModal = ({ open, onClose, onTasksGenerated }) => {
  const [taskDescription, setTaskDescription] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [generatedTasks, setGeneratedTasks] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [imageThumbnail, setImageThumbnail] = useState(null);
  const [listName, setListName] = useState('');
  const [error, setError] = useState(null);
  const [detailLevel, setDetailLevel] = useState(1);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  const detailLabels = ['Brief', 'Standard', 'Detailed'];

  const handleClose = () => {
    setTaskDescription('');
    setGeneratedTasks(null);
    setListName('');
    setSelectedImage(null);
    setImageThumbnail(null);
    setError(null);
    setDetailLevel(1);
    onClose();
  };

  const handleImageUpload = async (event) => {
    const file = event.target.files[0];
    if (file) {
      try {
        const resizedFile = await resizeImage(file);
        setSelectedImage(resizedFile);
        setImageThumbnail(URL.createObjectURL(resizedFile));
        setError(null);
      } catch (err) {
        console.error('Error resizing image:', err);
        setError(`Failed to process the image: ${err.message}`);
        setSelectedImage(null);
        setImageThumbnail(null);
      }
    }
  };

  const handleRemoveImage = () => {
    setSelectedImage(null);
    setImageThumbnail(null);
    setError(null);
  };

  const handleGenerate = async () => {
    if (!taskDescription.trim() && !selectedImage) {
      setError('Please provide a task description or upload an image.');
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      const data = await api.generateTasks(taskDescription, selectedImage, detailLevel);
      const tasks = Array.isArray(data) ? data : data.tasks;
      const name = Array.isArray(data) ? '' : (data.list_name || '');
      setGeneratedTasks(tasks);
      setListName(name);
    } catch (err) {
      console.error('Error generating tasks:', err);
      const errorMsg = typeof err.response?.data === 'string'
        ? err.response.data
        : err.message || 'An unexpected error occurred.';
      setError(errorMsg);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddTasks = async () => {
    if (generatedTasks) {
      setIsLoading(true);
      try {
        await onTasksGenerated(generatedTasks, listName);
        handleClose();
      } catch (err) {
        console.error('Error adding tasks:', err);
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();
    if (!generatedTasks) {
      handleGenerate();
    }
  };

  const renderContent = () => {
    if (generatedTasks) {
      return (
        <div>
          <h4>Generated Tasks:</h4>
          <ul>
            {generatedTasks.map((task, index) => (
              <li key={index}>{task.label}</li>
            ))}
          </ul>
        </div>
      );
    }

    return (
      <Grid columns={imageThumbnail ? 2 : 1} divided>
        <Grid.Row>
          <Grid.Column width={imageThumbnail ? 10 : 16}>
            <Form onSubmit={handleFormSubmit} style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              <Form.Field style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                <label>Task List Description</label>
                <TextArea
                  placeholder="Describe the task list you want to generate..."
                  value={taskDescription}
                  onChange={(e) => setTaskDescription(e.target.value)}
                  disabled={isLoading}
                  style={{ flexGrow: 1, minHeight: '100px', resize: 'none' }}
                />
              </Form.Field>
              <Form.Field>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5em', flexWrap: 'wrap' }}>
                  <Button as="label" htmlFor="gen-file" icon type="button" basic>
                    <Icon name="file image outline" />
                    Upload Image
                  </Button>
                  <Button icon onClick={() => cameraInputRef.current.click()} type="button" basic>
                    <Icon name="camera" />
                    Take Picture
                  </Button>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5em', marginLeft: 'auto' }}>
                    <span style={{ fontSize: '0.85em', color: '#666', whiteSpace: 'nowrap' }}>{detailLabels[detailLevel]}</span>
                    <input
                      type="range"
                      min="0"
                      max="2"
                      value={detailLevel}
                      onChange={(e) => setDetailLevel(Number(e.target.value))}
                      style={{ width: '80px', cursor: 'pointer' }}
                    />
                  </div>
                </div>
                <input type="file" id="gen-file" hidden onChange={handleImageUpload} accept="image/*" ref={fileInputRef} />
                <input type="file" hidden onChange={handleImageUpload} accept="image/*" capture="environment" ref={cameraInputRef} />
              </Form.Field>
            </Form>
          </Grid.Column>
          {imageThumbnail && (
            <Grid.Column width={6}>
              <Image src={imageThumbnail} size="medium" />
              <Button icon labelPosition="left" onClick={handleRemoveImage} style={{ marginTop: '1em' }}>
                <Icon name="remove" />
                Remove Image
              </Button>
            </Grid.Column>
          )}
        </Grid.Row>
      </Grid>
    );
  };

  return (
    <StyledModal open={open} onClose={handleClose} size="small">
      <Modal.Header>Generate Task List</Modal.Header>
      <Modal.Content>
        {error && (
          <Message negative>
            <Message.Header>Error</Message.Header>
            <p>{error}</p>
          </Message>
        )}
        {renderContent()}
      </Modal.Content>
      <Modal.Actions>
        <Button onClick={handleClose}>Cancel</Button>
        {!generatedTasks ? (
          <Button
            primary
            onClick={handleGenerate}
            loading={isLoading}
            disabled={(!taskDescription.trim() && !selectedImage) || isLoading}
          >
            Generate Tasks
          </Button>
        ) : (
          <Button primary onClick={handleAddTasks} loading={isLoading}>
            Add Tasks
          </Button>
        )}
      </Modal.Actions>
    </StyledModal>
  );
};

export default TaskGenerationModal;
